import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useCattle } from '@/contexts/CattleContext';
import { useReproduction } from '@/contexts/ReproductionContext';
import { Heart, Calendar, Activity, AlertCircle, Plus, FileText, TrendingUp } from 'lucide-react';
import PregnancyDialog from '@/components/ganado/PregnancyDialog';
import { toast } from 'sonner';

export default function ReproductionManagement() {
  const { cattle } = useCattle();
  const { pregnancies, addPregnancy, getActivePregnancy } = useReproduction();
  const [selectedCattle, setSelectedCattle] = useState(null);
  const [showPregnancyDialog, setShowPregnancyDialog] = useState(false);

  // Filtrar solo hembras
  const females = cattle?.filter(c => c.gender === 'Hembra') || [];
  
  // Filtrar solo machos (toros)
  const bulls = cattle?.filter(c => c.gender === 'Macho') || [];

  // Estadísticas
  const activePregnancies = pregnancies.filter(p => p.status === 'active').length;
  const completedBirths = pregnancies.filter(p => p.status === 'completed').length;
  const abortions = pregnancies.filter(p => p.status === 'aborted').length;

  const handleRegisterPregnancy = (cattle) => {
    // Verificar si ya tiene un embarazo activo
    const activePregnancy = getActivePregnancy(cattle.id);
    if (activePregnancy) {
      toast.error('Esta vaca ya tiene un embarazo activo');
      return;
    }

    setSelectedCattle(cattle);
    setShowPregnancyDialog(true);
  };

  const handleSavePregnancy = (pregnancyData) => {
    addPregnancy(pregnancyData);
    toast.success('Embarazo registrado exitosamente');
  };

  const getPregnancyStatus = (cattleId) => {
    const pregnancy = getActivePregnancy(cattleId);
    if (!pregnancy) return null;

    const daysPregnant = Math.floor(
      (new Date() - new Date(pregnancy.conceptionDate)) / (1000 * 60 * 60 * 24)
    );

    return {
      ...pregnancy,
      daysPregnant,
      trimester: daysPregnant < 94 ? 1 : daysPregnant < 188 ? 2 : 3
    };
  };

  const statusColors = {
    active: 'bg-rose-100 text-rose-800 border-rose-200',
    completed: 'bg-green-100 text-green-800 border-green-200',
    aborted: 'bg-gray-100 text-gray-800 border-gray-200'
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-[1400px]">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-rose-400 to-rose-600 flex items-center justify-center shadow-md">
            <Heart className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Gestión de Reproducción</h1>
            <p className="text-muted-foreground">Control de embarazos y partos</p>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="border-border/50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Hembras Totales</p>
                <p className="text-2xl font-bold">{females.length}</p>
              </div>
              <Activity className="h-8 w-8 text-rose-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Embarazos Activos</p>
                <p className="text-2xl font-bold text-rose-600">{activePregnancies}</p>
              </div>
              <Heart className="h-8 w-8 text-rose-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Partos Completados</p>
                <p className="text-2xl font-bold text-green-600">{completedBirths}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Abortos</p>
                <p className="text-2xl font-bold">{abortions}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista de Hembras */}
      <Card className="border-border/50">
        <CardHeader>
          <CardTitle>Vacas Registradas</CardTitle>
        </CardHeader>
        <CardContent>
          {females.length > 0 ? (
            <div className="space-y-3">
              {females.map(cow => {
                const pregnancyStatus = getPregnancyStatus(cow.id);
                
                return (
                  <div key={cow.id} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-rose-100 to-rose-200 flex items-center justify-center">
                        <span className="text-lg font-bold text-rose-600">{cow.name.charAt(0)}</span>
                      </div>
                      <div>
                        <p className="font-semibold">{cow.name}</p>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span>{cow.tag}</span>
                          <span>•</span>
                          <span>{cow.breed}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      {pregnancyStatus ? (
                        <div className="text-right mr-4">
                          <Badge variant="outline" className="bg-rose-100 text-rose-800 border-rose-200 mb-1">
                            Embarazada - {pregnancyStatus.daysPregnant} días
                          </Badge>
                          <p className="text-xs text-muted-foreground">
                            {pregnancyStatus.trimester}° Trimestre
                          </p>
                        </div>
                      ) : (
                        <Badge variant="outline" className="mr-4">
                          Disponible
                        </Badge>
                      )}

                      <Button
                        size="sm"
                        onClick={() => handleRegisterPregnancy(cow)}
                        disabled={!!pregnancyStatus}
                        className="bg-rose-600 hover:bg-rose-700"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        {pregnancyStatus ? 'Ya Embarazada' : 'Registrar Embarazo'}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <Heart className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground">No hay hembras registradas</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog de Registro de Embarazo */}
      {selectedCattle && (
        <PregnancyDialog
          open={showPregnancyDialog}
          onOpenChange={setShowPregnancyDialog}
          cattle={selectedCattle}
          bulls={bulls}
          onSave={handleSavePregnancy}
        />
      )}
    </div>
  );
}
